// Client wrappers for the login-gated weigh-in webform's remaining direct
// table writes.
//
// SCOPE. src/webforms/WeighInsWebform.tsx routes its weigh_ins /
// weigh_in_sessions lifecycle through the SECURITY DEFINER RPCs in
// src/lib/weighInApi.ts (migrations 195/196). What is left is the cattle
// DIRECTORY side of the chute workflow -- creating a cow row for a "+ New Cow"
// entry, swapping a tag on a retag or a reconcile, and keeping the cow's
// cattle_comments note in step with the entry's note. The cattle writes are
// ordinary authenticated table writes and stay PostgREST chains. The three
// cattle_comments writes are SECURITY DEFINER RPCs since migration 220 (S2):
// the S2 table takes direct cattle_comments writes away, and a PostgREST
// UPDATE/DELETE on rows a policy hides affects 0 rows WITHOUT an error. This
// module owns the transport, the form owns every decision about when to call it.
//
// WHAT DELIBERATELY DOES NOT MOVE. The best-effort audit emissions that follow
// each write (recordActivityEvent for the new cow, recordFieldChange for both
// tag swaps), the local setCattleList / setEntries reflects, the gating that
// makes an idempotent retag issue no write at all, and the ordering that keeps
// the entry's new_tag_flag from clearing before the cow is actually retagged --
// all of that is control flow and all of it stays in the form.
//
// WHY EVERY FUNCTION RETURNS AN ENVELOPE AND NONE OF THEM THROW. Two distinct
// reasons, both load-bearing:
//
//   1. The cattle writes are checked, and their failure text is built from the
//      PostgREST message ("Could not create cow: " + error.message). Returning
//      the envelope keeps that string byte-identical.
//   2. The cattle_comments writes are SOFT-FAIL. Each sits inside a
//      `try { ... } catch { /* table may not exist yet */ }` that never reads
//      the result, so today a database error there is silently ignored and the
//      operator's save still succeeds. A throwing wrapper would change that in
//      one place that matters: the note REPLACE runs a delete and an insert
//      inside a single try, and today a failed delete still lets the insert
//      run. Throwing would skip it. So these return the envelope too, and the
//      caller keeps discarding it exactly as before.

import type {SupabaseClient, SupabaseErrorLike} from './supabaseTypes.js';
import {
  applyCattleOperation,
  cattleRejectionMessage,
  newCattleRequestId,
  type CattleOpOutcome,
} from './cattleOperationsApi.js';

// Rows cross this boundary as caller-composed column bags -- the form builds
// them from its own state and the database validates them.
export type WeighInWebformRow = Record<string, unknown>;

// A PostgREST write envelope, narrowed to the one field the call sites read.
export type WeighInWebformWriteResult = {data?: unknown; error?: SupabaseErrorLike | null};

// The mig-055 SECURITY DEFINER stamp of week4Lbs / week6Lbs onto the matching
// broiler batch row. Public/webform code cannot reach the admin batch store
// under anon RLS, which is the whole reason this is an RPC.
//
// NOTE: this is the ONE wrapper in the weigh-in family that does not throw on
// RPC error, and that is deliberate. Its caller distinguishes three outcomes,
// not two: `error` aborts the completion with "Batch avg stamp failed: ",
// `data.applied === false` is a benign no-op the operator must not be blocked
// by (no entries, or no matching batch row), and anything else is success.
// Collapsing that into throw-or-return would lose the middle case.
export type BroilerBatchAvgStampResult = {
  data?: {applied?: boolean; reason?: string} | null;
  error?: SupabaseErrorLike | null;
};

// `sessionId` is admitted as possibly-undefined rather than asserted to be a
// string. That mirrors the caller exactly: WeighInsWebform holds the session as
// `{id?: string}` and passes `session.id` straight through here today, with no
// narrowing between the completion RPC and this stamp. Tightening the parameter
// would move a runtime failure the server currently rejects into a client-side
// assertion the call site never made.
export async function stampBroilerBatchAvg(
  sb: SupabaseClient,
  sessionId: string | undefined,
): Promise<BroilerBatchAvgStampResult> {
  if (!sb) throw new Error('stampBroilerBatchAvg: sb required');
  return await sb.rpc('stamp_broiler_batch_avg', {session_id_in: sessionId});
}

// Map one 245 operation outcome onto this module's raw envelope. The callers
// read `.error.message` to stop the save (checked writes), so a refusal keeps
// its user-facing sentence (the pig contract's shape) and a `mustReread`
// outcome — no confirmed reply — reads like today's transport failure.
function outcomeToEnvelope(outcome: CattleOpOutcome): WeighInWebformWriteResult {
  switch (outcome.status) {
    case 'saved':
    case 'no_change':
      return {data: outcome, error: null};
    case 'conflict':
      return {
        data: null,
        error: {message: 'This cow changed since you loaded the form. Reopen the entry and try again.'},
      };
    case 'rejected':
      return {data: null, error: {message: cattleRejectionMessage(outcome, 'animal')}};
    case 'not_attached':
      return {data: null, error: {message: 'That cow is no longer in the cattle list. Reload the form.'}};
    case 'unknown':
      return {data: null, error: {message: outcome.message}};
  }
}

// "+ New Cow": the cattle directory row is created BEFORE the weigh-in entry
// so the entry references a real cow. The caller aborts the save on failure.
// Migration 245: the write goes through the audited cattle_animal_operation
// (request-id receipted; a double submit replays the first answer).
export async function insertCattleAnimal(
  sb: SupabaseClient,
  row: WeighInWebformRow,
): Promise<WeighInWebformWriteResult> {
  if (!sb) throw new Error('insertCattleAnimal: sb required');
  const outcome = await applyCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'animal.create',
    target: {source: 'weigh_in_webform'},
    change: row,
  });
  return outcomeToEnvelope(outcome);
}

// The tag swap behind both a retag entry and a replacement-tag reconcile.
// Migration 245 (C2): the SERVER composes old_tags under the row lock from the
// declared source — the client no longer writes the array. `expectedTag` pins
// the tag as the caller read it; a stale value comes back as a conflict.
// H1 (REVIEW-244-v1): `priorTag` is the tag the OPERATOR TYPED and drives the
// server's composition — required for the weigh_in source.
export async function retagCattleAnimal(
  sb: SupabaseClient,
  cattleId: string,
  expectedTag: string | null,
  newTag: string,
  priorTag: string,
): Promise<WeighInWebformWriteResult> {
  if (!sb) throw new Error('retagCattleAnimal: sb required');
  if (!priorTag || !priorTag.trim()) throw new Error('retagCattleAnimal: priorTag (the typed tag) is required');
  const expected: WeighInWebformRow = {};
  if (expectedTag !== undefined) expected.tag = expectedTag;
  const outcome = await applyCattleOperation(sb, {
    requestId: newCattleRequestId(),
    operation: 'animal.retag',
    target: {cattle_id: cattleId},
    expected: expected as Record<string, unknown>,
    change: {tag: newTag, source: 'weigh_in', prior_tag: priorTag.trim()},
  });
  return outcomeToEnvelope(outcome);
}

// SOFT-FAIL. cattle_id may legitimately be null: in replacement-tag mode the
// cow is not known yet and reconcileWeighInComment stitches it on later. The
// row is forwarded unchanged; insert_cattle_weigh_in_comment (migration 220)
// accepts only source 'weigh_in' rows that carry a reference_id.
export async function insertWeighInComment(
  sb: SupabaseClient,
  comment: WeighInWebformRow,
): Promise<WeighInWebformWriteResult> {
  if (!sb) throw new Error('insertWeighInComment: sb required');
  return await sb.rpc('insert_cattle_weigh_in_comment', {p_record: comment});
}

// SOFT-FAIL. reassign_cattle_weigh_in_comment (migration 220) re-points only
// the entry's WEIGH-IN note: source = 'weigh_in' AND reference_id, both terms,
// and only cattle_id / cattle_tag. The direct update it replaced was keyed by
// reference_id alone; S2 narrowed that on purpose, since the reconcile only
// ever stitches the cow onto this form's own note.
export async function reassignWeighInComment(
  sb: SupabaseClient,
  referenceId: string,
  patch: WeighInWebformRow,
): Promise<WeighInWebformWriteResult> {
  if (!sb) throw new Error('reassignWeighInComment: sb required');
  return await sb.rpc('reassign_cattle_weigh_in_comment', {p_reference_id: referenceId, p_patch: patch});
}

// SOFT-FAIL. BOTH filter terms are required: dropping the source term would
// delete comments this webform does not own. delete_cattle_weigh_in_comments
// (migration 220) deletes source = 'weigh_in' AND reference_id, both terms.
export async function deleteWeighInComments(
  sb: SupabaseClient,
  referenceId: string,
): Promise<WeighInWebformWriteResult> {
  if (!sb) throw new Error('deleteWeighInComments: sb required');
  return await sb.rpc('delete_cattle_weigh_in_comments', {p_reference_id: referenceId});
}
